import React, { useEffect, useState } from 'react';
import { RouteProp, useIsFocused, useNavigation, useRoute } from '../../navigation/native';
import { Image } from '../../web/react-native';
import styled from '../../components/styled';
import LinearGradient from '../../components/LinearGradient';
import Icon from '../../components/Icon';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';

import LogoImage from '../../assets/images/Logo.svg';

import * as Colors from '../../helpers/Colors';
import { LoadingOverlay, PressableWithOpacity, ActionButton } from '../../components';
import { PreferencesNavParamList } from './PreferencesNav';
import { Route } from '../../app/Route';
import AssetsModal from './AssetsModal';
import AssetTypePicker from './AssetTypePicker';
import {
  selectHasPreferences,
  selectHierarchy,
  selectIsPreferenceLoading,
  selectPreferencesError,
  selectUser,
  selectCurrentSiteCode,
  selectPreferences,
  preferenceActions,
} from '../../store';
import { displayInformationAlert } from '../../helpers/Functions';
import { AppDispatch } from '../../store/store';

const PreferencesScreen = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation();
  const route = useRoute<RouteProp<PreferencesNavParamList, 'Preferences'>>();
  const isFocused = useIsFocused();

  const user = selectUser.useHook();
  const isLoading = selectIsPreferenceLoading.useHook();
  const hierarchy = selectHierarchy.useHook();
  const [isBusy, setIsBusy] = useState(false);
  const errorMessage = selectPreferencesError.useHook();
  const hasPreferences = selectHasPreferences.useHook();
  const siteCode = selectCurrentSiteCode.useHook();
  const preferences = selectPreferences.useHook();
  const openedFromMenu = !!route.params?.fromMenu || !!route.params?.canNavigateBack;

  useEffect(() => {
    dispatch(preferenceActions.loadHierarchy()).then(() => {
      if (!openedFromMenu && hasPreferences && !errorMessage) {
        navigation.navigate(Route.reportScreen);
      }
    });
    if (siteCode) dispatch(preferenceActions.getPreferencesForSite(siteCode));
  }, []);

  useEffect(() => {
    if (errorMessage)
      displayInformationAlert({
        title: 'Error',
        description: errorMessage,
        onOk: () => dispatch(preferenceActions.resetErrorMessage()),
      });
  }, [dispatch, errorMessage]);

  const handleSaveButtonPressed = () => {
    setIsBusy(true);
    dispatch(
      preferenceActions.savePreferences({
        preferences,
        selectedAsset: preferences.selectedAsset.assetName,
      }),
    )
      .then(unwrapResult)
      .then(() => {
        navigation.navigate(Route.reportScreen);
      })
      .catch((_err) => {})
      .finally(() => setIsBusy(false));
  };

  const handleRefreshButtonPressed = () => {
    setIsBusy(true);
    dispatch(preferenceActions.loadHierarchy()).finally(() => setIsBusy(false));
  };

  // Opened from Edit/menu: the saved asset tree stays usable while it is refreshed in the background.
  const refreshInBackground = openedFromMenu && !!hierarchy;
  const showLoading = isFocused && (isBusy || (isLoading && !refreshInBackground));

  return (
    <Container>
      <LoadingOverlay visible={showLoading} />
      <AssetsModal />
      <Header>
        <Logo />
        <SubtitleContainer>
          <WelcomeContainer>
            <WelcomeText>Welcome</WelcomeText>
            <NameText>{user?.name}</NameText>
          </WelcomeContainer>

          <ButtonContainer>
            <IconButton onPress={handleRefreshButtonPressed}>
              <RetryIcon />
            </IconButton>
            {route.params?.canNavigateBack && (
              <IconButton
                onPress={() => {
                  navigation.navigate(Route.reportScreen);
                }}>
                <BackIcon />
              </IconButton>
            )}
          </ButtonContainer>
        </SubtitleContainer>
      </Header>

      <Background>
        <AssetTypePicker />        
        <SaveButton disabled={!hasPreferences} onPress={handleSaveButtonPressed}>
          Save and Continue
        </SaveButton>
      </Background>
    </Container>
  );
};

export default PreferencesScreen;

const Container = styled.ScrollView.attrs({})`
  display: flex;
  height: 100%; 
  flex: 1;
`;

const Header = styled.View`
  flex-direction: column;
  height: 200px;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 40px;
  justify-content: space-around;
`;

const Logo = styled(Image).attrs({ source: LogoImage })``;

const SubtitleContainer = styled.View`
  flex-direction: row !important;  
  justify-content: space-between;
`;

const WelcomeContainer = styled.View`
  flex-direction: column;
  justify-content: center;
`;

const WelcomeText = styled.Text`
  color: #fff;
`;

const NameText = styled.Text`
  color: #fff;
  font-size: 18px;
`;

const ButtonContainer = styled.View`
  flex-direction: row !important;
`;

const IconButton = styled(PressableWithOpacity).attrs({
  hitSlop: 10,
})`
  align-self: center;
  margin: auto;
`;

const RetryIcon = styled(Icon).attrs({
  name: 'refresh',
  color: 'white',
})`
  font-size: 35px;
`;

const BackIcon = styled(Icon).attrs({
  name: 'arrow-forward',
  color: 'white',
})`
  font-size: 35px;
`;

const Background = styled.View`
  padding-left: 40px;
  padding-right: 40px;
  padding-top: 30px;
  padding-bottom: calc(40px + env(safe-area-inset-bottom));
  background-color: #faf3f6;
  flex: 1;
`;

const SaveButton = styled(ActionButton)<{ disabled?: boolean }>`
  background: ${Colors.primary};
`;
